import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { CheckCircle, Loader2, Image, Gift, ShoppingCart, Plus, Clock, Camera, Package, Minus, CreditCard, X, ZoomIn, ChevronLeft, ChevronRight, ArrowUpCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const PreOrderReviewPage = () => {
  const { reviewToken } = useParams();
  const [searchParams] = useSearchParams();
  const addonConfirmedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState({}); // {itemIndex: photoId} for single-photo products
  const [multiSelectedPhotos, setMultiSelectedPhotos] = useState({}); // {itemIndex: {front: photoId, back: photoId}} for trader cards
  const [packageItemPhotos, setPackageItemPhotos] = useState({}); // {"itemIndex-pkgItemIndex": photoId} for package items
  const [packageItemMultiPhotos, setPackageItemMultiPhotos] = useState({}); // {"itemIndex-pkgItemIndex": {front, back}} for trader cards in packages
  const [selectedBackgrounds, setSelectedBackgrounds] = useState({}); // {"itemIndex" or "itemIndex-pkgItemIndex": backgroundId}
  const [customizations, setCustomizations] = useState({}); // {"itemIndex" or "itemIndex-pkgItemIndex": {field: value}}
  const [showUpsellDialog, setShowUpsellDialog] = useState(false);
  const [approving, setApproving] = useState(false);
  
  // Bonus items selection state
  const [bonusWalletPhoto, setBonusWalletPhoto] = useState(null); // Single photo for both wallets
  const [bonusWalletBackground, setBonusWalletBackground] = useState(null); // Single background for both wallets
  const [freeDigitalPhoto, setFreeDigitalPhoto] = useState(null);
  const [freeDigitalBackground, setFreeDigitalBackground] = useState(null); // Background for free digital
  const [lightboxIndex, setLightboxIndex] = useState(null); // click-to-enlarge photo viewer
  
  // Add-on cart state
  const [addonCart, setAddonCart] = useState([]);
  const [showAddonCheckout, setShowAddonCheckout] = useState(false);
  const [processingAddon, setProcessingAddon] = useState(false);

  // Upgrade state
  const [upgradeDialog, setUpgradeDialog] = useState(null); // {itemIndex, item}
  const [upgradeMode, setUpgradeMode] = useState('swap'); // 'swap' | 'variation'
  const [upgradeTargetId, setUpgradeTargetId] = useState(null); // 'product:<id>' | 'package:<id>'
  const [upgradeVariations, setUpgradeVariations] = useState({});
  const [upgradeQty, setUpgradeQty] = useState(1);
  const [processingUpgrade, setProcessingUpgrade] = useState(false);

  // Delivery for physical add-on items (orders that skipped delivery originally, e.g. team photo only)
  const [addonDelivery, setAddonDelivery] = useState({ option: '', address: '', city: '', postal_code: '', address_type: '', notes: '' });
  const ADDON_DELIVERY_OPTIONS = [
    { id: 'local', name: 'Personal Local Delivery', description: 'In Spruce Grove/Stony Plain', fee: 5 },
    { id: 'rural', name: 'Personal Delivery', description: 'Within 20km of Stony Plain/Rural', fee: 8 },
    { id: 'mail', name: 'Direct from Lab Delivery', description: 'Letter Rate (Max 8x12/500g)', fee: 10 }
  ];

  useEffect(() => {
    // Check for addon payment success/cancel BEFORE fetching, so merged data is loaded once
    if (searchParams.get('addon_success') === 'true') {
      confirmAddonPayment();
    } else if (searchParams.get('addon_cancelled') === 'true') {
      toast.error('Add-on payment was cancelled');
      clearAddonItems();
    } else if (searchParams.get('upgrade_success') === 'true') {
      confirmUpgradePayment();
    } else if (searchParams.get('upgrade_cancelled') === 'true') {
      toast.error('Upgrade payment was cancelled');
      cancelUpgradeRequest();
    } else if (searchParams.get('admin_payment_success') === 'true') {
      confirmAdminPayment();
    } else {
      fetchPreorderData();
    }
  }, [reviewToken]);

  const confirmAdminPayment = async () => {
    try {
      const res = await axios.post(`${API}/preorder/review/${reviewToken}/confirm-admin-payment`);
      if (!res.data?.already_confirmed) {
        toast.success('Payment received — thank you!', { duration: 8000 });
      }
      window.history.replaceState({}, '', `/preorder/review/${reviewToken}`);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.info('Your payment is still processing. Please refresh this page in a moment.', { duration: 8000 });
      }
    } finally {
      fetchPreorderData();
    }
  };

  const confirmUpgradePayment = async () => {
    try {
      const res = await axios.post(`${API}/preorder/review/${reviewToken}/confirm-upgrade-payment`);
      if (!res.data?.already_confirmed) {
        toast.success('Payment received! Your item has been upgraded — choose its photo and background, then approve your order.', { duration: 10000 });
      }
      window.history.replaceState({}, '', `/preorder/review/${reviewToken}`);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.info('Your payment is still processing. Please refresh this page in a moment.', { duration: 10000 });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to confirm upgrade payment');
      }
    } finally {
      fetchPreorderData();
    }
  };

  const cancelUpgradeRequest = async () => {
    try {
      await axios.delete(`${API}/preorder/review/${reviewToken}/upgrade-request`);
      window.history.replaceState({}, '', `/preorder/review/${reviewToken}`);
    } catch (error) {
      console.error('Failed to cancel upgrade:', error);
    } finally {
      fetchPreorderData();
    }
  };

  const payForUpgrade = async () => {
    setProcessingUpgrade(true);
    try {
      const res = await axios.post(`${API}/preorder/review/${reviewToken}/upgrade-checkout`, null, {
        params: { origin_url: window.location.origin }
      });
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start payment');
      setProcessingUpgrade(false);
    }
  };

  const openUpgrade = (itemIndex, item) => {
    setUpgradeDialog({ itemIndex, item });
    setUpgradeMode('swap');
    setUpgradeTargetId(null);
    setUpgradeVariations({});
    setUpgradeQty(item.quantity || 1);
  };

  const submitUpgrade = async (body) => {
    setProcessingUpgrade(true);
    try {
      const res = await axios.post(`${API}/preorder/review/${reviewToken}/request-upgrade`, body);
      if (res.data.applied) {
        toast.success('Item swapped! Choose its photo and background below.', { duration: 8000 });
        setUpgradeDialog(null);
        fetchPreorderData();
      } else if (res.data.pending_admin) {
        toast.success("Upgrade request sent! We'll review it and email you once it's approved.", { duration: 8000 });
        setUpgradeDialog(null);
        fetchPreorderData();
      } else if (res.data.requires_payment) {
        const co = await axios.post(`${API}/preorder/review/${reviewToken}/upgrade-checkout`, null, {
          params: { origin_url: window.location.origin }
        });
        window.location.href = co.data.checkout_url;
        return;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request upgrade');
    }
    setProcessingUpgrade(false);
  };

  const confirmAddonPayment = async () => {
    try {
      const response = await axios.post(`${API}/preorder/review/${reviewToken}/confirm-addon-payment`);
      if (!response.data?.already_confirmed) {
        toast.success("Payment received! Your new items are confirmed — don't forget to approve your order.", { duration: 10000 });
      }
      setAddonCart([]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Clear URL params
      window.history.replaceState({}, '', `/preorder/review/${reviewToken}`);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.info('Your payment is still processing. Please refresh this page in a moment to see your new items.', { duration: 10000 });
      } else if (error.response?.status !== 400) {
        // 400 = already confirmed (e.g. page refresh after success) - not an error for the parent
        toast.error('Failed to confirm add-on payment');
      }
    } finally {
      // Always load fresh data AFTER confirmation to avoid a stale-data race
      fetchPreorderData();
    }
  };

  const clearAddonItems = async () => {
    try {
      await axios.delete(`${API}/preorder/review/${reviewToken}/addon-items`);
      window.history.replaceState({}, '', `/preorder/review/${reviewToken}`);
    } catch (error) {
      console.error('Failed to clear addon items:', error);
    } finally {
      fetchPreorderData();
    }
  };

  // Digital credit swap (post print-deadline)
  const [swapMode, setSwapMode] = useState('items');
  const [swapSelections, setSwapSelections] = useState({});
  const [swapSubmitting, setSwapSubmitting] = useState(false);

  const submitDigitalSwap = async () => {
    setSwapSubmitting(true);
    try {
      await axios.post(`${API}/preorder/review/${reviewToken}/digital-swap`, {
        mode: swapMode,
        selections: swapMode === 'items' ? swapSelections : null
      });
      toast.success('Digital products selected! Now choose your photos and backgrounds below, then approve your order.', { duration: 9000 });
      setSwapSelections({});
      fetchPreorderData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to select digital products');
    } finally {
      setSwapSubmitting(false);
    }
  };

  const renderDigitalSwapCard = () => {
    const swap = data?.digital_swap;
    if (!swap?.enabled || swap.done) return null;
    const digitalProducts = (data?.available_products || [])
      .filter(p => (p.category || '') === 'digital' && !(p.name || '').toLowerCase().includes('all images'));
    const chosenCount = Object.values(swapSelections).reduce((s, q) => s + (q || 0), 0);
    return (
      <Card className="border-2 border-purple-300 bg-purple-50/60" data-testid="digital-swap-card">
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-purple-900 text-lg">Print deadline has passed — choose your digital products</h3>
            <p className="text-sm text-purple-800 mt-1">
              Your payment of <strong>${swap.paid_total.toFixed(2)}</strong> gives you a credit for up to{' '}
              <strong>{swap.allowance_items} digital item{swap.allowance_items === 1 ? '' : 's'}</strong>. Your products will be
              custom-designed and delivered as high-resolution digital images by email.
            </p>
          </div>
          {swap.all_images_eligible && (
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="swap-mode-all">
                <input type="radio" name="swap-mode" className="mt-1" checked={swapMode === 'all_images'} onChange={() => setSwapMode('all_images')} />
                <span className="text-sm text-slate-700">
                  <strong>All digital images</strong> with your chosen background (a $90 value)
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer" data-testid="swap-mode-items">
                <input type="radio" name="swap-mode" className="mt-1" checked={swapMode === 'items'} onChange={() => setSwapMode('items')} />
                <span className="text-sm text-slate-700">
                  <strong>Choose up to {swap.allowance_items} custom digital items</strong> (memory mate, hockey card, detailed image...)
                </span>
              </label>
            </div>
          )}
          {swapMode === 'items' && (
            <div className="space-y-2">
              {digitalProducts.map(p => (
                <div key={p.id} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-purple-200 p-3" data-testid={`swap-product-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 text-sm">{p.name}</p>
                    {p.description && <p className="text-xs text-slate-500 truncate">{p.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                      onClick={() => setSwapSelections(prev => ({ ...prev, [p.id]: Math.max(0, (prev[p.id] || 0) - 1) }))}
                      data-testid={`swap-minus-${p.id}`}>-</Button>
                    <span className="w-5 text-center text-sm font-medium">{swapSelections[p.id] || 0}</span>
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                      disabled={chosenCount >= swap.allowance_items}
                      onClick={() => setSwapSelections(prev => ({ ...prev, [p.id]: (prev[p.id] || 0) + 1 }))}
                      data-testid={`swap-plus-${p.id}`}>+</Button>
                  </div>
                </div>
              ))}
              <p className="text-xs text-purple-700">{chosenCount} of {swap.allowance_items} items chosen</p>
            </div>
          )}
          <Button
            className="w-full bg-purple-700 hover:bg-purple-800 text-white"
            disabled={swapSubmitting || (swapMode === 'items' && chosenCount === 0)}
            onClick={submitDigitalSwap}
            data-testid="confirm-digital-swap-btn"
          >
            {swapSubmitting ? 'Saving...' : swapMode === 'all_images' ? 'Choose All Digital Images' : `Confirm ${chosenCount} Digital Item${chosenCount === 1 ? '' : 's'}`}
          </Button>
          <p className="text-xs text-slate-500">After confirming, select your photo and background for each item below, then approve your order.</p>
        </CardContent>
      </Card>
    );
  };

  const fetchPreorderData = async () => {
    try {
      const response = await axios.get(`${API}/preorder/review/${reviewToken}`);
      setData(response.data);
      
      // Initialize selected photos, backgrounds, and customizations from items
      if (response.data.preorder?.items) {
        const initial = {};
        const pkgInitial = {};
        const bgInitial = {};
        const customInitial = {};
        
        response.data.preorder.items.forEach((item, idx) => {
          if (item.package_items && item.package_items.length > 0) {
            // Package item - initialize each sub-item
            item.package_items.forEach((pkgItem, pkgIdx) => {
              const key = `${idx}-${pkgIdx}`;
              if (pkgItem.selected_photo_id) {
                pkgInitial[key] = pkgItem.selected_photo_id;
              }
              if (pkgItem.multi_photos) {
                // Multi-photo selection (trader cards, blankets in packages)
                setPackageItemMultiPhotos(prev => ({ ...prev, [key]: pkgItem.multi_photos }));
              }
              if (pkgItem.selected_background_id) {
                bgInitial[key] = pkgItem.selected_background_id;
              }
              if (pkgItem.customizations) {
                customInitial[key] = pkgItem.customizations;
              }
            });
          } else {
            // Regular product
            if (item.selected_photo_id) {
              initial[idx] = item.selected_photo_id;
            }
            if (item.multi_photos) {
              // Multi-photo selection (trader cards, blankets)
              setMultiSelectedPhotos(prev => ({ ...prev, [idx]: item.multi_photos }));
            }
            if (item.selected_background_id) {
              bgInitial[idx] = item.selected_background_id;
            }
            if (item.customizations) {
              customInitial[idx] = item.customizations;
            }
          }
        });
        
        setSelectedPhotos(initial);
        setPackageItemPhotos(pkgInitial);
        setSelectedBackgrounds(bgInitial);
        setCustomizations(customInitial);
        
        // Load bonus wallet selections if saved (now single selection for both wallets)
        const preorder = response.data.preorder;
        if (preorder.bonus_wallet_photos?.photo1) {
          setBonusWalletPhoto(preorder.bonus_wallet_photos.photo1);
        }
        if (preorder.bonus_wallet_backgrounds?.bg1) {
          setBonusWalletBackground(preorder.bonus_wallet_backgrounds.bg1);
        }
        if (preorder.free_digital_photo_id) {
          setFreeDigitalPhoto(preorder.free_digital_photo_id);
        }
        if (preorder.free_digital_background_id) {
          setFreeDigitalBackground(preorder.free_digital_background_id);
        }
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load pre-order');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const isUpgradableItem = (item) =>
    (item.price || 0) > 0 && !/team\s*photo/i.test(item.product_name || item.package_name || '');

  const itemIsPhysical = (item) => {
    if (item.package_items?.length) {
      return item.package_items.some(pi => {
        const pd = getProductDetails(pi.product_id);
        return pd && pd.category !== 'digital';
      });
    }
    const pd = getProductDetails(item.product_id);
    return pd ? pd.category !== 'digital' : false;
  };

  const addonNeedsDelivery = () =>
    !preorder?.delivery?.option &&
    (preorder?.items || []).some(i => i.addon_pending_payment && itemIsPhysical(i));

  const renderAddonPaymentBanner = () => {
    if (preorder?.addon_payment_status !== 'unpaid' || !(preorder?.addon_total > 0)) return null;
    const pendingCount = (preorder.items || []).filter(i => i.addon_pending_payment).length;
    if (pendingCount === 0) return null;
    const needsDelivery = addonNeedsDelivery();
    const selOpt = ADDON_DELIVERY_OPTIONS.find(o => o.id === addonDelivery.option);
    const displayTotal = (preorder.addon_total || 0) + (needsDelivery && selOpt ? selOpt.fee : 0);
    return (
      <Card className="border-green-400 bg-green-50" data-testid="addon-payment-banner">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div>
              <p className="font-semibold text-sm text-stone-800">
                {pendingCount} new item{pendingCount > 1 ? 's' : ''} added — payment required
              </p>
              <p className="text-xs text-stone-600">
                Choose a photo and background for each new item below, then pay {formatCurrency(displayTotal)} to confirm them.
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handlePayForAddons} disabled={processingAddon} data-testid="pay-addons-btn">
                {processingAddon ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CreditCard className="h-3 w-3 mr-1" />}
                Pay for Added Items — {formatCurrency(displayTotal)}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelPendingAddons} data-testid="cancel-addons-btn">Remove</Button>
            </div>
          </div>
          {needsDelivery && (
            <div className="border-t border-green-200 pt-3 space-y-2" data-testid="addon-delivery-form">
              <p className="text-sm font-semibold text-stone-800">Delivery for your new items</p>
              <p className="text-xs text-stone-600">Your new items need to be delivered — choose a method (added to your payment):</p>
              <div className="grid sm:grid-cols-3 gap-2">
                {ADDON_DELIVERY_OPTIONS.map(o => (
                  <div
                    key={o.id}
                    onClick={() => setAddonDelivery(prev => ({ ...prev, option: o.id }))}
                    className={`border rounded-lg p-2 cursor-pointer text-xs transition-colors ${addonDelivery.option === o.id ? 'border-green-600 bg-white ring-1 ring-green-400' : 'bg-white hover:border-stone-300'}`}
                    data-testid={`addon-delivery-${o.id}`}
                  >
                    <p className="font-semibold">{o.name} — ${o.fee}</p>
                    <p className="text-stone-500">{o.description}</p>
                  </div>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input placeholder="Street address" value={addonDelivery.address} onChange={e => setAddonDelivery(p => ({ ...p, address: e.target.value }))} className="h-9 bg-white" data-testid="addon-delivery-address" />
                <Input placeholder="City" value={addonDelivery.city} onChange={e => setAddonDelivery(p => ({ ...p, city: e.target.value }))} className="h-9 bg-white" data-testid="addon-delivery-city" />
                <Input placeholder="Postal code" value={addonDelivery.postal_code} onChange={e => setAddonDelivery(p => ({ ...p, postal_code: e.target.value }))} className="h-9 bg-white" data-testid="addon-delivery-postal" />
                <select
                  className="h-9 border rounded-md px-2 text-sm bg-white"
                  value={addonDelivery.address_type}
                  onChange={e => setAddonDelivery(p => ({ ...p, address_type: e.target.value }))}
                  data-testid="addon-delivery-address-type"
                >
                  <option value="">Address type...</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>
              <Input placeholder="Delivery notes (optional)" value={addonDelivery.notes} onChange={e => setAddonDelivery(p => ({ ...p, notes: e.target.value }))} className="h-9 bg-white" data-testid="addon-delivery-notes" />
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderUpgradeBanner = (ur) => {
    if (!ur) return null;
    const oldName = ur.old_item?.package_name || ur.old_item?.product_name;
    const newName = ur.new_item?.package_name || ur.new_item?.product_name;
    const payable = (ur.status === 'approved_awaiting_payment' || ur.status === 'awaiting_payment' || ur.status === 'ready') && ur.total_difference > 0;
    return (
      <Card className={ur.status === 'approved_awaiting_payment' ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'} data-testid="upgrade-banner">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <p className="font-semibold text-sm text-stone-800">
              {ur.status === 'pending_admin' ? 'Upgrade request pending approval' :
                ur.status === 'approved_awaiting_payment' ? 'Your upgrade was approved!' : 'Complete your upgrade'}
            </p>
            <p className="text-xs text-stone-600">
              {oldName} → {newName}{ur.total_difference > 0 ? ` (+${formatCurrency(ur.total_difference)})` : ''}
            </p>
            {ur.status === 'pending_admin' && (
              <p className="text-xs text-stone-500 mt-1">We'll email you once it's been reviewed.</p>
            )}
          </div>
          <div className="flex gap-2">
            {payable && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={payForUpgrade} disabled={processingUpgrade} data-testid="pay-upgrade-btn">
                {processingUpgrade ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CreditCard className="h-3 w-3 mr-1" />}
                Pay {formatCurrency(ur.total_difference)}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={cancelUpgradeRequest} data-testid="cancel-upgrade-btn">Cancel</Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAdminPaymentBanner = (apr) => {
    if (!apr || apr.status !== 'pending') return null;
    return (
      <Card className="border-amber-400 bg-amber-50" data-testid="admin-payment-banner">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <p className="font-semibold text-sm text-stone-800">Payment requested: {formatCurrency(apr.amount)}</p>
            <p className="text-xs text-stone-600">{apr.description}</p>
          </div>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 shrink-0"
            onClick={() => { window.location.href = apr.checkout_url; }}
            data-testid="pay-admin-request-btn"
          >
            <CreditCard className="h-3 w-3 mr-1" />
            Pay {formatCurrency(apr.amount)}
          </Button>
        </CardContent>
      </Card>
    );
  };

  const renderUpgradeDialog = (products, packages, taxRate, isPostApproval) => {
    if (!upgradeDialog) return null;
    const item = upgradeDialog.item;
    const qty = item.quantity || 1;
    const currentProduct = item.product_id ? (products || []).find(p => p.id === item.product_id) : null;
    const canAddOptions = !!(currentProduct?.variations?.length);
    const candidates = [
      ...(products || []).map(p => ({ ...p, _type: 'product' })),
      ...(packages || []).map(p => ({ ...p, _type: 'package' }))
    ]
      .filter(c => !(c.name || '').toUpperCase().includes('TEAM PHOTO'))
      .filter(c => !(c._type === 'product' && c.id === item.product_id) && !(c._type === 'package' && c.id === item.package_id))
      .sort((a, b) => a.price - b.price);

    const [targetType, targetId] = (upgradeTargetId || ':').split(':');
    const target = targetType === 'product'
      ? (products || []).find(p => p.id === targetId)
      : (packages || []).find(p => p.id === targetId);

    let diff = 0;
    let missingRequired = false;
    let body = null;
    if (upgradeMode === 'swap' && target) {
      let targetPrice = target.price || 0;
      if (targetType === 'product') {
        for (const v of target.variations || []) {
          const sel = upgradeVariations[v.name];
          const opt = (v.options || []).find(o => o.name === sel);
          if (opt) targetPrice += opt.price_adjustment || 0;
          else if (v.required) missingRequired = true;
        }
      }
      diff = targetPrice * upgradeQty - (item.price || 0) * qty;
      body = {
        item_index: upgradeDialog.itemIndex,
        upgrade_type: 'swap',
        new_product_id: targetType === 'product' ? target.id : null,
        new_package_id: targetType === 'package' ? target.id : null,
        selected_variations: targetType === 'product' ? upgradeVariations : {},
        quantity: upgradeQty
      };
    } else if (upgradeMode === 'variation' && currentProduct) {
      const existing = item.selected_variations || {};
      let delta = 0;
      for (const v of currentProduct.variations || []) {
        const sel = upgradeVariations[v.name];
        if (!sel || sel === existing[v.name]) continue;
        const newOpt = (v.options || []).find(o => o.name === sel);
        const oldOpt = existing[v.name] ? (v.options || []).find(o => o.name === existing[v.name]) : null;
        delta += (newOpt?.price_adjustment || 0) - (oldOpt?.price_adjustment || 0);
      }
      diff = delta * qty;
      const changed = Object.keys(upgradeVariations).some(k => upgradeVariations[k] && upgradeVariations[k] !== existing[k]);
      body = changed ? {
        item_index: upgradeDialog.itemIndex,
        upgrade_type: 'variation',
        selected_variations: { ...existing, ...upgradeVariations }
      } : null;
    }
    const taxDiff = diff * ((taxRate || 0) / 100);
    const totalDue = Math.round((diff + taxDiff) * 100) / 100;
    const canSubmit = !!body && !missingRequired && diff >= 0 && !processingUpgrade;

    return (
      <Dialog open={!!upgradeDialog} onOpenChange={(open) => !open && setUpgradeDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpCircle className="h-5 w-5 text-blue-600" />
              Upgrade: {item.package_name || item.product_name}
            </DialogTitle>
            <DialogDescription>
              Swap for something of equal or greater value, or add options like magnetic backing. You only pay the difference — downgrades and refunds aren't available.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button
              variant={upgradeMode === 'swap' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setUpgradeMode('swap'); setUpgradeVariations({}); }}
              data-testid="upgrade-mode-swap"
            >
              Swap Item
            </Button>
            {canAddOptions && (
              <Button
                variant={upgradeMode === 'variation' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setUpgradeMode('variation'); setUpgradeTargetId(null); setUpgradeVariations({}); }}
                data-testid="upgrade-mode-options"
              >
                Add Options
              </Button>
            )}
          </div>
          {upgradeMode === 'swap' ? (
            <div className="space-y-3">
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {candidates.length === 0 && (
                  <p className="text-sm text-stone-500">No eligible items to swap to.</p>
                )}
                {candidates.map(c => {
                  const key = `${c._type}:${c.id}`;
                  const rowDiff = c.price * upgradeQty - (item.price || 0) * qty;
                  return (
                    <div
                      key={key}
                      onClick={() => { setUpgradeTargetId(key); setUpgradeVariations({}); }}
                      className={`flex justify-between items-center p-3 border rounded-lg cursor-pointer transition-colors ${upgradeTargetId === key ? 'border-blue-500 bg-blue-50' : 'hover:border-slate-300'}`}
                      data-testid={`upgrade-candidate-${c.id}`}
                    >
                      <div>
                        <div className="font-medium text-sm flex items-center gap-1">
                          {c.name}
                          {c._type === 'package' && <Badge variant="secondary" className="text-xs">Package</Badge>}
                        </div>
                        <p className="text-xs text-stone-500">{formatCurrency(c.price)} each</p>
                      </div>
                      <span className={`text-sm font-semibold ${rowDiff > 0 ? 'text-amber-600' : rowDiff === 0 ? 'text-green-600' : 'text-stone-400'}`}>
                        {rowDiff > 0 ? `+${formatCurrency(rowDiff)}` : rowDiff === 0 ? 'Even swap' : 'Add options or quantity'}
                      </span>
                    </div>
                  );
                })}
              </div>
              {target && (
                <div className="flex items-center gap-3 border-t pt-3">
                  <Label className="text-xs text-stone-600">Quantity:</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setUpgradeQty(q => Math.max(1, q - 1))}
                    data-testid="upgrade-qty-minus"
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="text-sm font-semibold w-6 text-center" data-testid="upgrade-qty-value">{upgradeQty}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setUpgradeQty(q => q + 1)}
                    data-testid="upgrade-qty-plus"
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                  <span className="text-xs text-stone-500">
                    replacing {qty}x {item.product_name || item.package_name}
                  </span>
                </div>
              )}
              {targetType === 'product' && target?.variations?.length > 0 && (
                <div className="space-y-2 border-t pt-3">
                  <p className="text-xs font-medium text-stone-600">Options for {target.name}:</p>
                  {target.variations.map(v => (
                    <div key={v.name}>
                      <Label className="text-xs text-stone-600">
                        {v.name}{v.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <select
                        className="w-full border rounded-md h-9 px-2 text-sm mt-1 bg-white"
                        value={upgradeVariations[v.name] || ''}
                        onChange={e => setUpgradeVariations(prev => ({ ...prev, [v.name]: e.target.value }))}
                        data-testid={`upgrade-variation-${v.name}`}
                      >
                        <option value="">Select...</option>
                        {(v.options || []).map(o => (
                          <option key={o.name} value={o.name}>
                            {o.name}{(o.price_adjustment || 0) > 0 ? ` (+${formatCurrency(o.price_adjustment)})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {(currentProduct?.variations || []).map(v => {
                const existing = item.selected_variations || {};
                return (
                  <div key={v.name}>
                    <Label className="text-xs text-stone-600">
                      {v.name}
                      {existing[v.name] && <span className="text-stone-400 ml-1">(currently: {existing[v.name]})</span>}
                    </Label>
                    <select
                      className="w-full border rounded-md h-9 px-2 text-sm mt-1 bg-white"
                      value={upgradeVariations[v.name] ?? existing[v.name] ?? ''}
                      onChange={e => setUpgradeVariations(prev => ({ ...prev, [v.name]: e.target.value }))}
                      data-testid={`upgrade-addoption-${v.name}`}
                    >
                      <option value="">Select...</option>
                      {(v.options || []).map(o => (
                        <option key={o.name} value={o.name}>
                          {o.name}{(o.price_adjustment || 0) > 0 ? ` (+${formatCurrency(o.price_adjustment)})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
          <div className="border-t pt-3 space-y-2">
            {body && diff >= 0 && (
              <div className="flex justify-between text-sm" data-testid="upgrade-total-due">
                <span className="text-stone-600">Amount due now{taxRate > 0 ? ' (incl. tax)' : ''}:</span>
                <span className="font-bold text-stone-800">{totalDue > 0 ? formatCurrency(totalDue) : 'No charge'}</span>
              </div>
            )}
            {diff < 0 && (
              <p className="text-xs text-red-600">Downgrades aren't allowed — please choose something of equal or greater value.</p>
            )}
            {missingRequired && (
              <p className="text-xs text-amber-600">Please choose the required options above.</p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setUpgradeDialog(null)}>Cancel</Button>
              <Button
                disabled={!canSubmit}
                onClick={() => submitUpgrade(body)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="upgrade-submit-btn"
              >
                {processingUpgrade && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isPostApproval ? 'Request Upgrade' : totalDue > 0 ? `Pay ${formatCurrency(totalDue)} & Upgrade` : 'Swap Now — No Charge'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Add-on cart functions
  const addToAddonCart = (product) => {
    const hasVariations = product.variations && product.variations.length > 0;
    if (!hasVariations) {
      const existingIndex = addonCart.findIndex(item => item.id === product.id && !item.productData);
      if (existingIndex >= 0) {
        const newCart = [...addonCart];
        newCart[existingIndex].quantity += 1;
        setAddonCart(newCart);
      } else {
        setAddonCart([...addonCart, {
          id: product.id,
          name: product.name,
          price: product.price,
          quantity: 1,
          type: 'product'
        }]);
      }
      toast.success(`Added ${product.name} to cart`);
      return;
    }
    // Products with options (e.g. Magnetic Backing) get their own configurable row
    setAddonCart([...addonCart, {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      type: 'product',
      variations: {},
      productData: product
    }]);
    toast.success(`Added ${product.name} — choose your options in the cart below`);
  };

  const setAddonVariation = (index, variationName, optionName) => {
    const newCart = [...addonCart];
    const item = { ...newCart[index] };
    item.variations = { ...(item.variations || {}) };
    if (optionName) {
      item.variations[variationName] = optionName;
    } else {
      delete item.variations[variationName];
    }
    // Recalculate price from base + selected option adjustments
    let price = item.productData.price;
    for (const v of (item.productData.variations || [])) {
      const chosen = item.variations[v.name];
      if (chosen) {
        const opt = (v.options || []).find(o => o.name === chosen);
        if (opt) price += opt.price_adjustment || 0;
      }
    }
    item.price = price;
    newCart[index] = item;
    setAddonCart(newCart);
  };

  const updateAddonQuantity = (index, change) => {
    const newCart = [...addonCart];
    newCart[index].quantity += change;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    setAddonCart(newCart);
  };

  const removeFromAddonCart = (index) => {
    const newCart = [...addonCart];
    newCart.splice(index, 1);
    setAddonCart(newCart);
  };

  const calculateAddonTotal = () => {
    return addonCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handleAddonCheckout = async () => {
    if (addonCart.length === 0) {
      toast.error('Add items to your cart first');
      return;
    }

    setProcessingAddon(true);
    try {
      // Required options (e.g. Shape, Size) must be chosen before adding
      for (const item of addonCart) {
        for (const v of (item.productData?.variations || [])) {
          if (v.required && !item.variations?.[v.name]) {
            toast.error(`Please choose ${v.name} for ${item.name}`);
            setProcessingAddon(false);
            return;
          }
        }
      }
      const items = addonCart.map(item => ({
        product_id: item.type === 'product' ? item.id : null,
        package_id: item.type === 'package' ? item.id : null,
        quantity: item.quantity,
        selected_variations: item.variations || {},
        customizations: {}
      }));

      // Items are added to the order right away (pending payment) so the client
      // picks photos/backgrounds FIRST, then pays
      await axios.post(`${API}/preorder/review/${reviewToken}/add-items`, items);
      setAddonCart([]);
      await fetchPreorderData();
      toast.success("Items added to your order! Choose a photo and background for each new item above, then click 'Pay for Added Items'.", { duration: 10000 });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error('Failed to add items');
    }
    setProcessingAddon(false);
  };

  const handlePayForAddons = async () => {
    // Selections for the newly added items are required BEFORE payment
    const errors = [];
    const bgRequired = backgrounds && backgrounds.length > 0;
    const skipValidation = (pd, name) => !pd || pd.category === 'other' ||
      /team\s*photo/i.test(pd?.name || '') || /team\s*photo/i.test(name || '');
    const checkPendingEntry = (pd, label, key, photoValue, multiValue) => {
      const maxPhotos = pd.max_photos || 1;
      if (isAllImagesProduct(pd)) {
        // All photos delivered automatically
      } else if (pd.allows_multiple_photos && maxPhotos > 1) {
        const chosen = Object.values(multiValue || {}).filter(Boolean);
        if (chosen.length < maxPhotos) errors.push(`${label}: select all ${maxPhotos} photos`);
      } else if (!photoValue) {
        errors.push(`${label}: photo not selected`);
      }
      if (pd.allows_background && bgRequired && !selectedBackgrounds[key]) {
        errors.push(`${label}: background not selected`);
      }
    };
    preorder?.items?.forEach((item, index) => {
      if (!item.addon_pending_payment) return;
      if (item.package_items && item.package_items.length > 0) {
        item.package_items.forEach((pkgItem, pkgIdx) => {
          const pd = getProductDetails(pkgItem.product_id);
          if (skipValidation(pd, pkgItem.product_name)) return;
          const key = `${index}-${pkgIdx}`;
          checkPendingEntry(pd, pkgItem.product_name || pd.name, key, packageItemPhotos[key], packageItemMultiPhotos[key]);
        });
      } else {
        const pd = getProductDetails(item.product_id);
        if (skipValidation(pd, item.product_name)) return;
        checkPendingEntry(pd, item.product_name || pd.name, index, selectedPhotos[index], multiSelectedPhotos[index]);
      }
    });
    if (errors.length > 0) {
      const shown = errors.slice(0, 4).join('\n');
      const extra = errors.length > 4 ? `\n+ ${errors.length - 4} more...` : '';
      toast.error(`Please choose photos & backgrounds for your new items before paying:\n${shown}${extra}`, { duration: 8000 });
      return;
    }
    // Physical add-ons on orders without delivery need a delivery method
    const needsDelivery = addonNeedsDelivery();
    if (needsDelivery) {
      if (!addonDelivery.option) {
        toast.error('Please choose a delivery method for your new items');
        return;
      }
      if (!addonDelivery.address || !addonDelivery.city || !addonDelivery.postal_code) {
        toast.error('Please fill in your complete delivery address');
        return;
      }
      if (!addonDelivery.address_type) {
        toast.error('Please select residential or commercial for your address');
        return;
      }
      if (addonDelivery.option === 'mail' && addonDelivery.address.toLowerCase().includes('po box')) {
        toast.error('PO Boxes are not accepted for Direct from Lab delivery');
        return;
      }
    }
    setProcessingAddon(true);
    try {
      if (needsDelivery) {
        await axios.post(`${API}/preorder/review/${reviewToken}/set-delivery`, addonDelivery);
      }
      // Persist selections so they survive the Stripe redirect
      const saved = await handleSaveSelections();
      if (!saved) {
        setProcessingAddon(false);
        return;
      }
      const response = await axios.post(`${API}/preorder/review/${reviewToken}/addon-checkout`, null, {
        params: { origin_url: window.location.origin }
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingAddon(false);
    }
  };

  const handleCancelPendingAddons = async () => {
    if (!window.confirm('Remove the unpaid added items from your order?')) return;
    await clearAddonItems();
  };

  const handleSelectPhoto = (itemIndex, photoId) => {
    setSelectedPhotos(prev => ({ ...prev, [itemIndex]: photoId }));
  };

  const handleSelectMultiPhoto = (itemIndex, position, photoId) => {
    // For trader cards: position is 'front' or 'back'
    setMultiSelectedPhotos(prev => ({
      ...prev,
      [itemIndex]: { ...(prev[itemIndex] || {}), [position]: photoId }
    }));
  };

  const handleSelectPackageItemPhoto = (itemIndex, pkgItemIndex, photoId) => {
    setPackageItemPhotos(prev => ({ ...prev, [`${itemIndex}-${pkgItemIndex}`]: photoId }));
  };

  const handleSelectPackageItemMultiPhoto = (itemIndex, pkgItemIndex, position, photoId) => {
    const key = `${itemIndex}-${pkgItemIndex}`;
    setPackageItemMultiPhotos(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [position]: photoId }
    }));
  };

  const handleSelectBackground = (key, backgroundId) => {
    setSelectedBackgrounds(prev => ({ ...prev, [key]: backgroundId }));
  };

  const handleCustomizationChange = (key, field, value) => {
    setCustomizations(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value }
    }));
  };

  const handleSaveSelections = async () => {
    // Background is only required for products that actually allow backgrounds
    const bgRequiredForItem = (key) => {
      const parts = String(key).split('-');
      const item = preorder?.items?.[parseInt(parts[0])];
      if (!item) return false;
      let pd, name;
      if (parts.length === 2 && item.package_items) {
        const pkgItem = item.package_items[parseInt(parts[1])];
        pd = getProductDetails(pkgItem?.product_id);
        name = pkgItem?.product_name;
      } else {
        pd = getProductDetails(item.product_id);
        name = item.product_name;
      }
      if (!pd || isTeamPhotoProduct(pd, name)) return false;
      return !!pd.allows_background;
    };
    // Validate required backgrounds
    if (backgrounds && backgrounds.length > 0) {
      // Check package item backgrounds
      for (const [key, photoId] of Object.entries(packageItemPhotos)) {
        if (photoId && bgRequiredForItem(key) && !selectedBackgrounds[key]) {
          toast.error('Please select a background for all products');
          return false;
        }
      }
      
      // Check package item multi-photo backgrounds
      for (const [key, photos] of Object.entries(packageItemMultiPhotos)) {
        if (Object.keys(photos).length > 0 && bgRequiredForItem(key) && !selectedBackgrounds[key]) {
          toast.error('Please select a background for all products');
          return false;
        }
      }
      
      // Check standalone product backgrounds
      for (const [itemIndex, photoId] of Object.entries(selectedPhotos)) {
        if (photoId && bgRequiredForItem(itemIndex) && !selectedBackgrounds[parseInt(itemIndex)]) {
          toast.error('Please select a background for all products');
          return false;
        }
      }
      
      // Check standalone multi-photo backgrounds
      for (const [itemIndex, photos] of Object.entries(multiSelectedPhotos)) {
        if (Object.keys(photos).length > 0 && bgRequiredForItem(itemIndex) && !selectedBackgrounds[parseInt(itemIndex)]) {
          toast.error('Please select a background for all products');
          return false;
        }
      }
      
      // Check bonus wallet backgrounds (single selection for both wallets)
      if (preorder?.includes_free_wallets) {
        if (bonusWalletPhoto && !bonusWalletBackground) {
          toast.error('Please select a background for the Free Wallets');
          return false;
        }
      }
      
      // Check free digital background
      if (preorder?.includes_free_digital) {
        if (freeDigitalPhoto && !freeDigitalBackground) {
          toast.error('Please select a background for the Free Digital Image');
          return false;
        }
      }
    }
    
    try {
      const selections = [];
      
      // Add regular product selections (single photo)
      Object.entries(selectedPhotos).forEach(([itemIndex, photoId]) => {
        const key = parseInt(itemIndex);
        selections.push({
          item_index: key,
          photo_id: photoId,
          background_id: selectedBackgrounds[key] || null,
          customizations: customizations[key] || null
        });
      });
      
      // Add multi-photo selections (trader cards, blankets, etc.)
      Object.entries(multiSelectedPhotos).forEach(([itemIndex, photos]) => {
        const key = parseInt(itemIndex);
        // Store all selected photos
        if (Object.keys(photos).length > 0) {
          selections.push({
            item_index: key,
            photo_id: photos.front || Object.values(photos)[0] || null,
            multi_photos: photos, // Store all photo selections
            background_id: selectedBackgrounds[key] || null,
            customizations: { ...(customizations[key] || {}), multi_photos: photos }
          });
        }
      });
      
      // Add package item selections
      Object.entries(packageItemPhotos).forEach(([key, photoId]) => {
        const [itemIndex, pkgItemIndex] = key.split('-').map(Number);
        selections.push({
          item_index: itemIndex,
          package_item_index: pkgItemIndex,
          photo_id: photoId,
          background_id: selectedBackgrounds[key] || null,
          customizations: customizations[key] || null
        });
      });
      
      // Add package item multi-photo selections (trader cards, blankets in packages)
      Object.entries(packageItemMultiPhotos).forEach(([key, photos]) => {
        const [itemIndex, pkgItemIndex] = key.split('-').map(Number);
        if (Object.keys(photos).length > 0) {
          selections.push({
            item_index: itemIndex,
            package_item_index: pkgItemIndex,
            photo_id: photos.front || Object.values(photos)[0] || null,
            multi_photos: photos, // Store all photo selections
            background_id: selectedBackgrounds[key] || null,
            customizations: { ...(customizations[key] || {}), multi_photos: photos }
          });
        }
      });
      
      // Add bonus wallet selections if applicable (same photo/background for both wallets)
      if (preorder?.includes_free_wallets && bonusWalletPhoto) {
        selections.push({
          item_type: 'bonus_wallets',
          bonus_wallet_photos: { photo1: bonusWalletPhoto, photo2: bonusWalletPhoto },
          bonus_wallet_backgrounds: { bg1: bonusWalletBackground, bg2: bonusWalletBackground }
        });
      }
      
      // Add free digital photo and background selection if applicable
      if (preorder?.includes_free_digital && freeDigitalPhoto) {
        selections.push({
          item_type: 'free_digital',
          free_digital_photo_id: freeDigitalPhoto,
          free_digital_background_id: freeDigitalBackground
        });
      }
      
      await axios.post(`${API}/preorder/review/${reviewToken}/select-photos`, selections);
      toast.success('Photo selections saved');
      return true;
    } catch (error) {
      toast.error('Failed to save selections');
      return false;
    }
  };

  const validateAllSelections = () => {
    const errors = [];
    const bgRequired = backgrounds && backgrounds.length > 0;
    const checkEntry = (pd, label, key, photoValue, multiValue) => {
      const maxPhotos = pd.max_photos || 1;
      if (isAllImagesProduct(pd)) {
        // All photos delivered automatically - only the background is needed
      } else if (pd.allows_multiple_photos && maxPhotos > 1) {
        const chosen = Object.values(multiValue || {}).filter(Boolean);
        if (chosen.length < maxPhotos) errors.push(`${label}: select all ${maxPhotos} photos`);
      } else if (!photoValue) {
        errors.push(`${label}: photo not selected`);
      }
      if (pd.allows_background && bgRequired && !selectedBackgrounds[key]) {
        errors.push(`${label}: background not selected`);
      }
    };
    const skipValidation = (pd, name) => !pd || pd.category === 'other' ||
      /team\s*photo/i.test(pd.name || '') || /team\s*photo/i.test(name || '');
    preorder?.items?.forEach((item, index) => {
      if (item.package_items && item.package_items.length > 0) {
        item.package_items.forEach((pkgItem, pkgIdx) => {
          const pd = getProductDetails(pkgItem.product_id);
          if (skipValidation(pd, pkgItem.product_name)) return;
          const key = `${index}-${pkgIdx}`;
          checkEntry(pd, pkgItem.product_name || pd.name, key, packageItemPhotos[key], packageItemMultiPhotos[key]);
        });
      } else {
        const pd = getProductDetails(item.product_id);
        if (skipValidation(pd, item.product_name)) return;
        checkEntry(pd, item.product_name || pd.name, index, selectedPhotos[index], multiSelectedPhotos[index]);
      }
    });
    if (preorder?.includes_free_wallets) {
      if (!bonusWalletPhoto) errors.push('Free Wallets: photo not selected');
      else if (bgRequired && !bonusWalletBackground) errors.push('Free Wallets: background not selected');
    }
    if (preorder?.includes_free_digital) {
      if (!freeDigitalPhoto) errors.push('Free Digital Image: photo not selected');
      else if (bgRequired && !freeDigitalBackground) errors.push('Free Digital Image: background not selected');
    }
    return errors;
  };

  const handleApprove = async () => {
    const errors = validateAllSelections();
    if (errors.length > 0) {
      const shown = errors.slice(0, 4).join('\n');
      const extra = errors.length > 4 ? `\n+ ${errors.length - 4} more...` : '';
      toast.error(`Please complete all selections before approving:\n${shown}${extra}`, { duration: 8000 });
      return;
    }
    setApproving(true);
    try {
      // Save selections first - abort if the save didn't go through
      const saved = await handleSaveSelections();
      if (!saved) return;
      
      // Then approve
      const response = await axios.post(`${API}/preorder/review/${reviewToken}/approve`);
      
      if (response.data.free_digital_sent) {
        toast.success('Order approved! Your free digital image has been sent to your email!', { duration: 5000 });
      } else {
        toast.success('Order approved! We\'ll begin processing your order.');
      }
      fetchPreorderData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve order', { duration: 8000 });
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold text-stone-800 mb-2">Error</h2>
            <p className="text-stone-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Handle special statuses
  if (data.status === 'awaiting_photos') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Camera className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-stone-800 mb-2">Photos In Progress</h1>
            <p className="text-stone-600">
              Your photos are still being prepared. We'll email you when they're ready for review!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (data.status === 'approved' || data.status === 'fulfilled') {
    const apPre = data.preorder;
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white p-4">
        <div className="max-w-2xl mx-auto py-8 space-y-6">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-stone-800 mb-2">Order Complete!</h1>
              <p className="text-stone-600">
                Your order has been {data.status === 'fulfilled' ? 'fulfilled' : 'approved and is being processed'}.
              </p>
            </CardContent>
          </Card>
          {apPre && renderUpgradeBanner(apPre.upgrade_request)}
          {apPre && renderAdminPaymentBanner(apPre.admin_payment_request)}
          {apPre?.items?.length > 0 && (
            <Card data-testid="approved-upgrade-section">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowUpCircle className="h-5 w-5 text-blue-600" />
                  Want to upgrade an item?
                </CardTitle>
                <CardDescription>
                  You can swap any item for something of equal or greater value, or add options like magnetic backing — you only pay the difference. Upgrade requests on completed orders are reviewed by us before they're applied.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {apPre.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center border rounded-lg p-3" data-testid={`approved-item-${index}`}>
                    <div>
                      <p className="font-medium text-sm">
                        {item.package_name || item.product_name} <span className="text-stone-400">x{item.quantity}</span>
                      </p>
                      {item.upgraded_from && (
                        <p className="text-xs text-blue-600">Upgraded from {item.upgraded_from.name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-amber-600">{formatCurrency(item.price)}</span>
                      {isUpgradableItem(item) && !apPre.upgrade_request && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                          onClick={() => openUpgrade(index, item)}
                          data-testid={`upgrade-item-btn-${index}`}
                        >
                          <ArrowUpCircle className="h-3 w-3 mr-1" />
                          Upgrade
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {renderUpgradeDialog(data.available_products, data.available_packages, apPre?.tax_rate || 0, true)}
        </div>
      </div>
    );
  }

  const { preorder, available_products, available_packages, item_products, item_packages, backgrounds } = data;
  const assignedPhotos = preorder.assigned_photos || [];

  // Team photos are not eligible for digital downloads
  const isTeamPhoto = (photo) => photo.is_team_photo === true || /team[\s_-]*photo/i.test(photo.filename || '');
  const nonTeamPhotos = assignedPhotos.filter(p => !isTeamPhoto(p));
  const selectablePhotosFor = (productDetails) =>
    productDetails?.category === 'digital' ? nonTeamPhotos : assignedPhotos;

  // Digital product that automatically delivers ALL of the child's photos
  const isAllImagesProduct = (pd) => pd?.category === 'digital' && /all\s*images?/i.test(pd?.name || '');

  // Team photo products are fulfilled automatically - nothing for the parent to select
  const isTeamPhotoProduct = (pd, name) => /team\s*photo/i.test(pd?.name || '') || /team\s*photo/i.test(name || '');

  // Click-to-enlarge lightbox helpers
  const getFullPhotoUrl = (photo) => {
    const url = photo.url || photo.thumbnail_url;
    if (!url) return '';
    return url.startsWith('http') ? url : `${BASE_URL}${url}`;
  };
  const openLightbox = (photoId) => {
    const idx = assignedPhotos.findIndex(p => p.id === photoId);
    if (idx >= 0) setLightboxIndex(idx);
  };
  const ZoomButton = ({ photoId }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); openLightbox(photoId); }}
      className="absolute top-1 right-1 z-10 bg-black/50 hover:bg-black/75 rounded-full p-1.5 transition-colors"
      aria-label="View larger"
      data-testid={`zoom-photo-${photoId}`}
    >
      <ZoomIn className="h-3.5 w-3.5 text-white" />
    </button>
  );

  // Helper to get product details including customization fields and templates
  const getProductDetails = (productId) => {
    // Check active products first, then products owned by this order (may be deactivated)
    const product = available_products?.find(p => p.id === productId) ||
                    item_products?.find(p => p.id === productId);
    if (product) return product;
    
    // Also check in package items for templates
    for (const pkg of [...(available_packages || []), ...(item_packages || [])]) {
      for (const item of (pkg.items || [])) {
        if (item.product_id === productId && item.product) {
          return { ...item.product, template: item.template };
        }
      }
    }
    return null;
  };

  // Helper to get package template info for a specific item in a package
  const getPackageItemTemplate = (packageId, productId) => {
    const pkg = (available_packages || []).concat(item_packages || []).find(p => p.id === packageId);
    if (pkg) {
      const item = pkg.items?.find(i => i.product_id === productId);
      return item?.template || null;
    }
    return null;
  };

  // Get smart upsell suggestions - products NOT already in order
  const getUpsellSuggestions = () => {
    if (!available_products || available_products.length === 0) return [];
    
    const orderedProductIds = preorder.items
      .filter(item => item.product_id)
      .map(item => item.product_id);
    
    // Digital-only events suggest only digital products; regular events suggest everything
    return available_products
      .filter(p => !data?.event?.digital_only || (p.category || '') === 'digital')
      .filter(p => !orderedProductIds.includes(p.id))
      .filter(p => !(p.name || '').toUpperCase().includes('TEAM PHOTO'))
      .sort((a, b) => a.price - b.price)
      .slice(0, 3);
  };

  // Helper to get proper image URL
  const getPhotoUrl = (photo) => {
    const url = photo.thumbnail_url || photo.url;
    if (!url) return '';
    // If it's an external URL (starts with http), use as-is
    if (url.startsWith('http')) return url;
    // If it starts with /api/, use base URL
    if (url.startsWith('/api/')) return `${BASE_URL}${url}`;
    // Otherwise, prepend base URL
    return `${BASE_URL}${url}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-stone-800">Review Your Photos</h1>
          <p className="text-stone-600">Hi {preorder.client_name}, your photos for {preorder.child_name} are ready!</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {renderDigitalSwapCard()}
        {/* Assigned Photos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-5 w-5 text-amber-600" />
              Your Photos ({assignedPhotos.length})
            </CardTitle>
            <CardDescription>
              These are the photos available for your order — tap any photo to view it larger
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {assignedPhotos.map(photo => (
                <div
                  key={photo.id}
                  className="relative rounded-lg overflow-hidden border cursor-zoom-in group"
                  onClick={() => openLightbox(photo.id)}
                  data-testid={`gallery-photo-${photo.id}`}
                >
                  <img loading="lazy" decoding="async"
                    src={getPhotoUrl(photo)}
                    alt={photo.filename}
                    className="w-full aspect-square object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Photo Lightbox */}
        {lightboxIndex !== null && assignedPhotos[lightboxIndex] && (
          <div
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightboxIndex(null)}
            data-testid="photo-lightbox"
          >
            <button
              type="button"
              className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/25 rounded-full p-2 transition-colors"
              onClick={() => setLightboxIndex(null)}
              data-testid="lightbox-close-btn"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
            {assignedPhotos.length > 1 && (
              <>
                <button
                  type="button"
                  className="absolute left-2 sm:left-6 text-white bg-white/10 hover:bg-white/25 rounded-full p-2 transition-colors"
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + assignedPhotos.length) % assignedPhotos.length); }}
                  data-testid="lightbox-prev-btn"
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="h-7 w-7" />
                </button>
                <button
                  type="button"
                  className="absolute right-2 sm:right-6 text-white bg-white/10 hover:bg-white/25 rounded-full p-2 transition-colors"
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % assignedPhotos.length); }}
                  data-testid="lightbox-next-btn"
                  aria-label="Next photo"
                >
                  <ChevronRight className="h-7 w-7" />
                </button>
              </>
            )}
            <div className="max-h-full max-w-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
              <img loading="lazy" decoding="async"
                src={getFullPhotoUrl(assignedPhotos[lightboxIndex])}
                alt={assignedPhotos[lightboxIndex].filename}
                className="max-h-[82vh] max-w-[92vw] object-contain rounded"
              />
              <p className="text-white/70 text-sm">
                Photo {lightboxIndex + 1} of {assignedPhotos.length}
              </p>
            </div>
          </div>
        )}

        {/* Pending upgrade banner */}
        {renderUpgradeBanner(preorder.upgrade_request)}

        {/* Admin payment request banner */}
        {renderAdminPaymentBanner(preorder.admin_payment_request)}

        {/* Pending add-on payment banner */}
        {renderAddonPaymentBanner()}

        {/* Order Items - Select Photos */}
        <Card>
          <CardHeader>
            <CardTitle>Select Photos for Your Products</CardTitle>
            <CardDescription>
              Choose which photo you'd like for each item in your order
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {preorder.items.map((item, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      {item.package_name ? (
                        <>
                          <Package className="h-4 w-4 text-amber-600" />
                          {item.package_name}
                          <Badge variant="secondary" className="ml-2">Package</Badge>
                        </>
                      ) : (
                        item.product_name
                      )}
                      {item.addon_pending_payment && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-400" data-testid={`pending-payment-badge-${index}`}>
                          Payment required
                        </Badge>
                      )}
                    </h3>
                    <p className="text-sm text-stone-500">Quantity: {item.quantity}</p>
                    {item.selected_variations && Object.keys(item.selected_variations).length > 0 && (
                      <p className="text-sm text-stone-500">
                        {Object.entries(item.selected_variations).map(([k, v]) => `${k}: ${v}`).join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-amber-600">{formatCurrency(item.price)}</p>
                    {item.upgraded_from && (
                      <p className="text-xs text-blue-600">Upgraded from {item.upgraded_from.name}</p>
                    )}
                    {isUpgradableItem(item) && !preorder.upgrade_request && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-1 h-7 text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                        onClick={() => openUpgrade(index, item)}
                        data-testid={`upgrade-item-btn-${index}`}
                      >
                        <ArrowUpCircle className="h-3 w-3 mr-1" />
                        Upgrade
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Package Items - Individual photo selection */}
                {item.package_items && item.package_items.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-sm text-stone-600 font-medium">Select a photo for each item in this package:</p>
                    {item.package_items.map((pkgItem, pkgIdx) => {
                      const key = `${index}-${pkgIdx}`;
                      const productDetails = getProductDetails(pkgItem.product_id);
                      const allowsBackground = productDetails?.allows_background;
                      const customizationFields = productDetails?.customization_fields || [];
                      
                      return (
                        <div key={pkgIdx} className="bg-slate-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-sm font-medium text-stone-700">
                              {pkgItem.product_name}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              x{pkgItem.quantity}
                            </Badge>
                            {pkgItem.selected_variations && Object.keys(pkgItem.selected_variations).length > 0 && (
                              <span className="text-xs text-stone-500">
                                ({Object.entries(pkgItem.selected_variations).map(([k, v]) => `${k}: ${v}`).join(', ')})
                              </span>
                            )}
                          </div>
                          
                          {/* Product Template Preview */}
                          {(productDetails?.template || getPackageItemTemplate(item.package_id, pkgItem.product_id)) && (
                            <div className="mb-3 p-2 bg-white rounded border border-slate-200">
                              <p className="text-xs text-slate-500 mb-1">Product Shape/Template:</p>
                              <img loading="lazy" decoding="async" 
                                src={(productDetails?.template || getPackageItemTemplate(item.package_id, pkgItem.product_id))?.thumbnail_url || 
                                     (productDetails?.template || getPackageItemTemplate(item.package_id, pkgItem.product_id))?.url}
                                alt={`${pkgItem.product_name} template`}
                                className="h-20 object-contain"
                              />
                            </div>
                          )}
                          
                          {/* Photo Selection */}
                          {(() => {
                            const allowsMultiplePhotos = productDetails?.allows_multiple_photos;
                            const maxPhotos = productDetails?.max_photos || 1;
                            const pkgProductNameLower = (pkgItem.product_name || '').toLowerCase();
                            const isTraderCard = pkgProductNameLower.includes('trader') || 
                                                 (productDetails?.category === 'trader_card');
                            const isBlanket = pkgProductNameLower.includes('blanket') || pkgProductNameLower.includes('fleece');
                            const needsMultiplePhotos = allowsMultiplePhotos && maxPhotos > 1;
                            
                            if (!productDetails) {
                              return (
                                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`no-selection-note-${index}-${pkgIdx}`}>
                                  <p className="text-sm text-slate-500">No selection needed for this item.</p>
                                </div>
                              );
                            }
                            
                            if (isAllImagesProduct(productDetails)) {
                              return (
                                <div className="p-3 bg-green-50 border border-green-300 rounded-lg" data-testid={`all-images-note-${index}-${pkgIdx}`}>
                                  <p className="text-sm font-semibold text-green-800">You'll receive ALL of your child's photos — no photo selection needed!</p>
                                  <p className="text-xs text-green-700 mt-1">Just choose your background below and it will be applied to every image.</p>
                                </div>
                              );
                            }
                            
                            if (isTeamPhotoProduct(productDetails, pkgItem.product_name)) {
                              return (
                                <div className="p-3 bg-green-50 border border-green-300 rounded-lg" data-testid={`team-photo-note-${index}-${pkgIdx}`}>
                                  <p className="text-sm font-semibold text-green-800">Your team photo is prepared automatically — nothing to select here!</p>
                                </div>
                              );
                            }
                            
                            // Determine photo slot labels based on product type
                            const getPhotoSlotLabel = (slotIndex) => {
                              if (isTraderCard && maxPhotos === 2) {
                                return slotIndex === 0 ? 'FRONT of card' : 'BACK of card';
                              }
                              if (isBlanket) {
                                return `Photo ${slotIndex + 1} for blanket`;
                              }
                              return `Photo ${slotIndex + 1}`;
                            };
                            
                            // Colors for different slots
                            const slotColors = [
                              { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-500', ring: 'ring-amber-200', overlay: 'bg-amber-500/20' },
                              { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500', ring: 'ring-blue-200', overlay: 'bg-blue-500/20' },
                              { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-500', ring: 'ring-green-200', overlay: 'bg-green-500/20' },
                              { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-500', ring: 'ring-purple-200', overlay: 'bg-purple-500/20' },
                            ];
                            
                            if (needsMultiplePhotos) {
                              return (
                                <div className="space-y-3">
                                  {Array.from({ length: maxPhotos }, (_, slotIndex) => {
                                    const slotKey = slotIndex === 0 ? 'front' : slotIndex === 1 ? 'back' : `photo${slotIndex + 1}`;
                                    const colors = slotColors[slotIndex % slotColors.length];
                                    
                                    return (
                                      <div key={slotIndex}>
                                        <p className="text-xs font-medium text-stone-600 mb-2 flex items-center gap-2">
                                          <span className={`${colors.bg} ${colors.text} px-1.5 py-0.5 rounded text-xs font-semibold`}>
                                            {slotIndex + 1}
                                          </span>
                                          Select photo for {getPhotoSlotLabel(slotIndex)}:
                                        </p>
                                        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                                          {selectablePhotosFor(productDetails).map(photo => {
                                            const isSelected = packageItemMultiPhotos[key]?.[slotKey] === photo.id;
                                            return (
                                              <div
                                                key={photo.id}
                                                data-testid={`package-photo-${slotKey}-${index}-${pkgIdx}-${photo.id}`}
                                                className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                                                  isSelected 
                                                    ? `${colors.border} ring-2 ${colors.ring}` 
                                                    : 'border-transparent hover:border-slate-300'
                                                }`}
                                                onClick={() => handleSelectPackageItemMultiPhoto(index, pkgIdx, slotKey, photo.id)}
                                              >
                                                <ZoomButton photoId={photo.id} />
                                                <img loading="lazy" decoding="async"
                                                  src={getPhotoUrl(photo)}
                                                  alt={photo.filename}
                                                  className="w-full aspect-square object-cover"
                                                />
                                                {isSelected && (
                                                  <div className={`absolute inset-0 ${colors.overlay} flex items-center justify-center`}>
                                                    <CheckCircle className={`h-5 w-5 ${colors.text}`} />
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  
                                  {/* Selection Summary */}
                                  <div className="bg-white rounded p-2 text-xs border">
                                    <span className="text-stone-600">
                                      Selected: {Object.keys(packageItemMultiPhotos[key] || {}).length} of {maxPhotos}
                                    </span>
                                    <span className="mx-2">|</span>
                                    {Array.from({ length: maxPhotos }, (_, slotIndex) => {
                                      const slotKey = slotIndex === 0 ? 'front' : slotIndex === 1 ? 'back' : `photo${slotIndex + 1}`;
                                      const isSelected = packageItemMultiPhotos[key]?.[slotKey];
                                      const label = isTraderCard && maxPhotos === 2 
                                        ? (slotIndex === 0 ? 'F' : 'B')
                                        : `${slotIndex + 1}`;
                                      return (
                                        <span key={slotIndex} className={`${isSelected ? 'text-green-600' : 'text-stone-400'} ${slotIndex > 0 ? 'ml-2' : ''}`}>
                                          {label}:{isSelected ? '✓' : '–'}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            }
                            
                            // Standard single photo selection
                            return (
                              <>
                                <p className="text-xs text-stone-500 mb-2">Select photo:</p>
                                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                                  {selectablePhotosFor(productDetails).map(photo => {
                                    const isSelected = packageItemPhotos[key] === photo.id;
                                    return (
                                      <div
                                        key={photo.id}
                                        data-testid={`package-photo-${index}-${pkgIdx}-${photo.id}`}
                                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                                          isSelected 
                                            ? 'border-amber-500 ring-2 ring-amber-200' 
                                            : 'border-transparent hover:border-slate-300'
                                        }`}
                                        onClick={() => handleSelectPackageItemPhoto(index, pkgIdx, photo.id)}
                                      >
                                        <ZoomButton photoId={photo.id} />
                                        <img loading="lazy" decoding="async"
                                          src={getPhotoUrl(photo)}
                                          alt={photo.filename}
                                          className="w-full aspect-square object-cover"
                                        />
                                        {isSelected && (
                                          <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                                            <CheckCircle className="h-5 w-5 text-amber-500" />
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            );
                          })()}
                          
                          {/* Background Selection */}
                          {allowsBackground && backgrounds && backgrounds.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-200">
                              <p className="text-xs text-stone-500 mb-2">Select background: <span className="text-red-500">*</span></p>
                              <div className="grid grid-cols-5 md:grid-cols-8 gap-2">
                                {backgrounds.map(bg => (
                                  <div
                                    key={bg.id}
                                    className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                                      selectedBackgrounds[key] === bg.id 
                                        ? 'border-amber-500 ring-2 ring-amber-200' 
                                        : 'border-transparent hover:border-slate-300'
                                    }`}
                                    onClick={() => handleSelectBackground(key, bg.id)}
                                    data-testid={`package-background-${key}-${bg.id}`}
                                  >
                                    <img loading="lazy" decoding="async"
                                      src={bg.thumbnail_url || bg.url}
                                      alt={bg.name}
                                      className="w-full aspect-square object-cover"
                                    />
                                    {selectedBackgrounds[key] === bg.id && (
                                      <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                                        <CheckCircle className="h-4 w-4 text-amber-500" />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                              {!selectedBackgrounds[key] && (
                                <p className="text-xs text-red-500 mt-1">Please select a background</p>
                              )}
                            </div>
                          )}
                          
                          {/* Customization Fields */}
                          {customizationFields.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                              {/* Show optional message for wallets, 5x7s, 8x10s */}
                              {(() => {
                                const productNameLower = (pkgItem.product_name || '').toLowerCase();
                                const isTraderCard = productNameLower.includes('trader') || productNameLower.includes('trading card');
                                const isMemoryMate = productNameLower.includes('memory mate');
                                // Only show optional message for wallets, 5x7s, 8x10s that are NOT Memory Mates or Trader Cards
                                const isOptionalProduct = !isMemoryMate && !isTraderCard && (
                                  productNameLower.includes('wallet') || 
                                  productNameLower.includes('5x7') || 
                                  productNameLower.includes('8x10')
                                );
                                
                                // Determine which fields are required based on product type
                                const getFieldRequired = (fieldName) => {
                                  const fn = fieldName.toLowerCase();
                                  if (isTraderCard) {
                                    return fn !== 'position'; // All except position required
                                  }
                                  if (isMemoryMate) {
                                    return fn === 'name' || fn === 'number' || fn === 'team';
                                  }
                                  return false; // Optional for wallets, 5x7s, 8x10s
                                };
                                
                                return (
                                  <>
                                    {isOptionalProduct && (
                                      <p className="text-xs text-stone-500 italic pb-2">
                                        These customizations are optional. Fields completed will be displayed on the photo.
                                      </p>
                                    )}
                                    {customizationFields.map((field, fieldIdx) => {
                                      const fieldName = typeof field === 'string' ? field : field.name;
                                      const fieldLabel = typeof field === 'string' ? field.charAt(0).toUpperCase() + field.slice(1) : (field.label || field.name);
                                      const placeholder = typeof field === 'string' ? `Enter ${field}` : (field.placeholder || `Enter ${fieldLabel}`);
                                      const isRequired = getFieldRequired(fieldName);
                                      
                                      return (
                                        <div key={fieldIdx}>
                                          <Label className="text-xs text-stone-600">
                                            {fieldLabel}
                                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                                          </Label>
                                          <Input
                                            value={customizations[key]?.[fieldName] || ''}
                                            onChange={(e) => handleCustomizationChange(key, fieldName, e.target.value)}
                                            placeholder={placeholder}
                                            className="h-8 text-sm mt-1"
                                          />
                                        </div>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Regular product - photo, background, and customization selection */
                  (() => {
                    const productDetails = getProductDetails(item.product_id);
                    const allowsBackground = productDetails?.allows_background;
                    const customizationFields = productDetails?.customization_fields || [];
                    const allowsMultiplePhotos = productDetails?.allows_multiple_photos;
                    const maxPhotos = productDetails?.max_photos || 1;
                    const productNameLower = (item.product_name || '').toLowerCase();
                    const isTraderCard = productNameLower.includes('trader') || 
                                         (productDetails?.category === 'trader_card');
                    const isBlanket = productNameLower.includes('blanket') || productNameLower.includes('fleece');
                    
                    // Determine photo slot labels based on product type
                    const getPhotoSlotLabel = (slotIndex) => {
                      if (isTraderCard && maxPhotos === 2) {
                        return slotIndex === 0 ? 'FRONT of card' : 'BACK of card';
                      }
                      if (isBlanket) {
                        return `Photo ${slotIndex + 1} for blanket`;
                      }
                      return `Photo ${slotIndex + 1}`;
                    };
                    
                    // Colors for different slots
                    const slotColors = [
                      { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-500', ring: 'ring-amber-200', overlay: 'bg-amber-500/20' },
                      { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500', ring: 'ring-blue-200', overlay: 'bg-blue-500/20' },
                      { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-500', ring: 'ring-green-200', overlay: 'bg-green-500/20' },
                      { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-500', ring: 'ring-purple-200', overlay: 'bg-purple-500/20' },
                    ];
                    
                    // Check if product needs multiple photo selection
                    const needsMultiplePhotos = allowsMultiplePhotos && maxPhotos > 1;
                    
                    return (
                      <div className="space-y-3">
                        {/* Product Template Preview */}
                        {productDetails?.template && (
                          <div className="p-3 bg-white rounded-lg border border-slate-200">
                            <p className="text-sm text-slate-600 mb-2 font-medium">Product Shape/Template:</p>
                            <img loading="lazy" decoding="async" 
                              src={productDetails.template.thumbnail_url || productDetails.template.url}
                              alt={`${item.product_name} template`}
                              className="h-24 object-contain"
                            />
                          </div>
                        )}
                        
                        {/* Photo Selection */}
                        {!productDetails ? (
                          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`no-selection-note-${index}`}>
                            <p className="text-sm text-slate-500">No selection needed for this item.</p>
                          </div>
                        ) : isAllImagesProduct(productDetails) ? (
                          <div className="p-3 bg-green-50 border border-green-300 rounded-lg" data-testid={`all-images-note-${index}`}>
                            <p className="text-sm font-semibold text-green-800">You'll receive ALL of your child's photos — no photo selection needed!</p>
                            <p className="text-xs text-green-700 mt-1">Just choose your background below and it will be applied to every image.</p>
                          </div>
                        ) : isTeamPhotoProduct(productDetails, item.product_name) ? (
                          <div className="p-3 bg-green-50 border border-green-300 rounded-lg" data-testid={`team-photo-note-${index}`}>
                            <p className="text-sm font-semibold text-green-800">Your team photo is prepared automatically — nothing to select here!</p>
                          </div>
                        ) : needsMultiplePhotos ? (
                          // Multiple photo selection (trader cards, blankets, etc.)
                          <div className="space-y-4">
                            {Array.from({ length: maxPhotos }, (_, slotIndex) => {
                              const slotKey = slotIndex === 0 ? 'front' : slotIndex === 1 ? 'back' : `photo${slotIndex + 1}`;
                              const colors = slotColors[slotIndex % slotColors.length];
                              
                              return (
                                <div key={slotIndex}>
                                  <p className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                                    <span className={`${colors.bg} ${colors.text} px-2 py-0.5 rounded text-xs font-semibold`}>
                                      {slotIndex + 1}
                                    </span>
                                    Select photo for {getPhotoSlotLabel(slotIndex)}:
                                  </p>
                                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                                    {selectablePhotosFor(productDetails).map(photo => {
                                      const isSelected = multiSelectedPhotos[index]?.[slotKey] === photo.id;
                                      return (
                                        <div
                                          key={photo.id}
                                          data-testid={`product-photo-${slotKey}-${index}-${photo.id}`}
                                          className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                                            isSelected 
                                              ? `${colors.border} ring-2 ${colors.ring}` 
                                              : 'border-transparent hover:border-slate-300'
                                          }`}
                                          onClick={() => handleSelectMultiPhoto(index, slotKey, photo.id)}
                                        >
                                          <ZoomButton photoId={photo.id} />
                                          <img loading="lazy" decoding="async"
                                            src={getPhotoUrl(photo)}
                                            alt={photo.filename}
                                            className="w-full aspect-square object-cover"
                                          />
                                          {isSelected && (
                                            <div className={`absolute inset-0 ${colors.overlay} flex items-center justify-center`}>
                                              <CheckCircle className={`h-5 w-5 ${colors.text}`} />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                            
                            {/* Selection Summary */}
                            <div className="bg-slate-50 rounded-lg p-3 text-sm">
                              <p className="font-medium text-stone-700 mb-1">Your selections ({Object.keys(multiSelectedPhotos[index] || {}).length} of {maxPhotos}):</p>
                              <div className="flex flex-wrap gap-3">
                                {Array.from({ length: maxPhotos }, (_, slotIndex) => {
                                  const slotKey = slotIndex === 0 ? 'front' : slotIndex === 1 ? 'back' : `photo${slotIndex + 1}`;
                                  const isSelected = multiSelectedPhotos[index]?.[slotKey];
                                  const label = isTraderCard && maxPhotos === 2 
                                    ? (slotIndex === 0 ? 'Front' : 'Back')
                                    : `Photo ${slotIndex + 1}`;
                                  return (
                                    <span key={slotIndex} className={isSelected ? 'text-green-600' : 'text-stone-400'}>
                                      {label}: {isSelected ? '✓' : '–'}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Standard single photo selection
                          <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                            {selectablePhotosFor(productDetails).map(photo => (
                              <div
                                key={photo.id}
                                data-testid={`product-photo-${index}-${photo.id}`}
                                className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                                  selectedPhotos[index] === photo.id 
                                    ? 'border-amber-500 ring-2 ring-amber-200' 
                                    : 'border-transparent hover:border-slate-300'
                                }`}
                                onClick={() => handleSelectPhoto(index, photo.id)}
                              >
                                <ZoomButton photoId={photo.id} />
                                <img loading="lazy" decoding="async"
                                  src={getPhotoUrl(photo)}
                                  alt={photo.filename}
                                  className="w-full aspect-square object-cover"
                                />
                                {selectedPhotos[index] === photo.id && (
                                  <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                                    <CheckCircle className="h-5 w-5 text-amber-500" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Background Selection */}
                        {allowsBackground && backgrounds && backgrounds.length > 0 && (
                          <div className="pt-3 border-t border-slate-200">
                            <p className="text-xs text-stone-500 mb-2">Select background: <span className="text-red-500">*</span></p>
                            <div className="grid grid-cols-5 md:grid-cols-8 gap-2">
                              {backgrounds.map(bg => (
                                <div
                                  key={bg.id}
                                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                                    selectedBackgrounds[index] === bg.id 
                                      ? 'border-amber-500 ring-2 ring-amber-200' 
                                      : 'border-transparent hover:border-slate-300'
                                  }`}
                                  onClick={() => handleSelectBackground(index, bg.id)}
                                  data-testid={`product-background-${index}-${bg.id}`}
                                >
                                  <img loading="lazy" decoding="async"
                                    src={bg.thumbnail_url || bg.url}
                                    alt={bg.name}
                                    className="w-full aspect-square object-cover"
                                  />
                                  {selectedBackgrounds[index] === bg.id && (
                                    <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                                      <CheckCircle className="h-4 w-4 text-amber-500" />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            {!selectedBackgrounds[index] && (
                              <p className="text-xs text-red-500 mt-1">Please select a background</p>
                            )}
                          </div>
                        )}
                        
                        {/* Customization Fields */}
                        {customizationFields.length > 0 && (
                          <div className="pt-3 border-t border-slate-200 space-y-2">
                            {(() => {
                              const productNameLower = (item.product_name || '').toLowerCase();
                              const isTraderCard = productNameLower.includes('trader') || productNameLower.includes('trading card');
                              const isMemoryMate = productNameLower.includes('memory mate');
                              // Only show optional message for wallets, 5x7s, 8x10s that are NOT Memory Mates or Trader Cards
                              const isOptionalProduct = !isMemoryMate && !isTraderCard && (
                                productNameLower.includes('wallet') || 
                                productNameLower.includes('5x7') || 
                                productNameLower.includes('8x10')
                              );
                              
                              const getFieldRequired = (fieldName) => {
                                const fn = fieldName.toLowerCase();
                                if (isTraderCard) {
                                  return fn !== 'position';
                                }
                                if (isMemoryMate) {
                                  return fn === 'name' || fn === 'number' || fn === 'team';
                                }
                                return false;
                              };
                              
                              return (
                                <>
                                  {isOptionalProduct && (
                                    <p className="text-xs text-stone-500 italic pb-2">
                                      These customizations are optional. Fields completed will be displayed on the photo.
                                    </p>
                                  )}
                                  {customizationFields.map((field, fieldIdx) => {
                                    const fieldName = typeof field === 'string' ? field : field.name;
                                    const fieldLabel = typeof field === 'string' ? field.charAt(0).toUpperCase() + field.slice(1) : (field.label || field.name);
                                    const placeholder = typeof field === 'string' ? `Enter ${field}` : (field.placeholder || `Enter ${fieldLabel}`);
                                    const isRequired = getFieldRequired(fieldName);
                                    
                                    return (
                                      <div key={fieldIdx}>
                                        <Label className="text-xs text-stone-600">
                                          {fieldLabel}
                                          {isRequired && <span className="text-red-500 ml-1">*</span>}
                                        </Label>
                                        <Input
                                          value={customizations[index]?.[fieldName] || ''}
                                          onChange={(e) => handleCustomizationChange(index, fieldName, e.target.value)}
                                          placeholder={placeholder}
                                          className="h-8 text-sm mt-1"
                                        />
                                      </div>
                                    );
                                  })}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Free Bonuses */}
        <div className="space-y-3">
          {/* Free Digital Image - All pre-orders */}
          {preorder.includes_free_digital && (
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <Gift className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Free Web-Size Digital Image Included!</p>
                    <p className="text-sm text-green-700">Select which photo and background you'd like for your free digital (small watermark in corner)</p>
                  </div>
                </div>
                
                {/* Photo Selection for Free Digital */}
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <p className="text-xs text-stone-500 mb-2">Select your free digital photo:</p>
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                    {nonTeamPhotos.map(photo => (
                      <div
                        key={photo.id}
                        data-testid={`free-digital-photo-${photo.id}`}
                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                          freeDigitalPhoto === photo.id 
                            ? 'border-green-500 ring-2 ring-green-200' 
                            : 'border-transparent hover:border-slate-300'
                        }`}
                        onClick={() => setFreeDigitalPhoto(photo.id)}
                      >
                        <ZoomButton photoId={photo.id} />
                        <img loading="lazy" decoding="async"
                          src={getPhotoUrl(photo)}
                          alt={photo.filename}
                          className="w-full aspect-square object-cover"
                        />
                        {freeDigitalPhoto === photo.id && (
                          <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-green-600 mt-2">
                    {freeDigitalPhoto ? '✓ Photo selected' : 'No photo selected yet'}
                  </p>
                </div>
                
                {/* Background Selection for Free Digital */}
                {backgrounds.length > 0 && (
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <p className="text-xs text-stone-500 mb-2">Select background: <span className="text-red-500">*</span></p>
                    <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                      {backgrounds.map(bg => (
                        <div
                          key={bg.id}
                          data-testid={`free-digital-background-${bg.id}`}
                          className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                            freeDigitalBackground === bg.id 
                              ? 'border-green-500 ring-2 ring-green-200' 
                              : 'border-transparent hover:border-slate-300'
                          }`}
                          onClick={() => setFreeDigitalBackground(bg.id)}
                        >
                          <img loading="lazy" decoding="async"
                            src={`${API}/files/${bg.url.split('/api/files/')[1]}`}
                            alt={bg.name}
                            className="w-full aspect-square object-cover"
                          />
                          {freeDigitalBackground === bg.id && (
                            <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-green-600 mt-2">
                      {freeDigitalBackground 
                        ? `✓ Background: ${backgrounds.find(b => b.id === freeDigitalBackground)?.name || 'Selected'}` 
                        : <span className="text-red-500">Please select a background</span>}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Free Wallets - $50+ orders */}
          {preorder.includes_free_wallets && (
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <Gift className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">2 Free Wallet-Sized Photos Included!</p>
                    <p className="text-sm text-green-700">Both wallets will use the same photo and background</p>
                  </div>
                </div>
                
                {/* Single Selection for Both Wallets */}
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <p className="text-sm font-medium text-stone-700 mb-3">
                    Select photo for your 2 Free Wallets
                  </p>
                  
                  {/* Photo Selection */}
                  <p className="text-xs text-stone-500 mb-2">Select photo:</p>
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mb-3">
                    {assignedPhotos.map(photo => (
                      <div
                        key={photo.id}
                        data-testid={`bonus-wallet-photo-${photo.id}`}
                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                          bonusWalletPhoto === photo.id 
                            ? 'border-green-500 ring-2 ring-green-200' 
                            : 'border-transparent hover:border-slate-300'
                        }`}
                        onClick={() => setBonusWalletPhoto(photo.id)}
                      >
                        <ZoomButton photoId={photo.id} />
                        <img loading="lazy" decoding="async"
                          src={getPhotoUrl(photo)}
                          alt={photo.filename}
                          className="w-full aspect-square object-cover"
                        />
                        {bonusWalletPhoto === photo.id && (
                          <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Background Selection */}
                  {backgrounds.length > 0 && (
                    <>
                      <p className="text-xs text-stone-500 mb-2">Select background: <span className="text-red-500">*</span></p>
                      <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                        {backgrounds.map(bg => (
                          <div
                            key={bg.id}
                            data-testid={`bonus-wallet-bg-${bg.id}`}
                            className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                              bonusWalletBackground === bg.id 
                                ? 'border-green-500 ring-2 ring-green-200' 
                                : 'border-transparent hover:border-slate-300'
                            }`}
                            onClick={() => setBonusWalletBackground(bg.id)}
                          >
                            <img loading="lazy" decoding="async"
                              src={bg.thumbnail_url || bg.url}
                              alt={bg.name}
                              className="w-full aspect-square object-cover"
                            />
                            {bonusWalletBackground === bg.id && (
                              <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {!bonusWalletBackground && (
                        <p className="text-xs text-red-500 mt-1">Please select a background</p>
                      )}
                    </>
                  )}
                </div>
                
                {/* Selection Summary */}
                <div className="text-xs text-green-700">
                  <span>2 Wallets: {bonusWalletPhoto ? '✓ Photo selected' : '– No photo selected'} {bonusWalletBackground ? '✓ Background selected' : ''}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Order Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Order Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {preorder.items.map((item, idx) => (
                <div key={idx} className="flex justify-between">
                  <span>{item.package_name || item.product_name} x{item.quantity}</span>
                  <span>{formatCurrency(item.price * item.quantity)}</span>
                </div>
              ))}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatCurrency(preorder.subtotal)}</span>
                </div>
                {preorder.tax > 0 && (
                  <div className="flex justify-between text-stone-500">
                    <span>Tax</span>
                    <span>{formatCurrency(preorder.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg mt-2">
                  <span>Total Paid</span>
                  <span className="text-green-600">{formatCurrency(preorder.total)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Smart Upsell Section */}
        {getUpsellSuggestions().length > 0 && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <span className="text-amber-500">★</span>
                Most parents also add...
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {getUpsellSuggestions().map(product => (
                  <div 
                    key={product.id}
                    className="bg-white rounded-lg p-3 border border-amber-200 flex flex-col"
                  >
                    <h4 className="font-medium text-stone-800 text-sm">{product.name}</h4>
                    <p className="text-amber-600 font-bold mt-1">Only {formatCurrency(product.price)}!</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2 border-amber-300 hover:bg-amber-100"
                      onClick={() => addToAddonCart(product)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add to Order
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add-on Cart */}
        {addonCart.length > 0 && (
          <Card className="border-green-300 bg-green-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-green-600" />
                Add-ons to Purchase
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {addonCart.map((item, index) => (
                  <div key={index} className="bg-white rounded-lg p-2 border">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.name}</p>
                        <p className="text-xs text-stone-500">{formatCurrency(item.price)} each</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => updateAddonQuantity(index, -1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-sm w-6 text-center">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => updateAddonQuantity(index, 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500"
                          onClick={() => removeFromAddonCart(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      <p className="text-sm font-semibold ml-3">{formatCurrency(item.price * item.quantity)}</p>
                    </div>
                    {(item.productData?.variations || []).map(v => (
                      <div key={v.name} className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-stone-600 whitespace-nowrap">
                          {v.name}{v.required ? ' *' : ''}:
                        </span>
                        <select
                          className="text-xs border border-stone-300 rounded px-2 py-1 bg-white flex-1"
                          value={item.variations?.[v.name] || ''}
                          onChange={(e) => setAddonVariation(index, v.name, e.target.value || null)}
                          data-testid={`addon-variation-${index}-${v.name.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <option value="">{v.required ? `Choose ${v.name}...` : 'No thanks'}</option>
                          {(v.options || []).map(opt => (
                            <option key={opt.name} value={opt.name}>
                              {opt.name}{opt.price_adjustment ? ` (+${formatCurrency(opt.price_adjustment)})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-semibold">Add-on Total:</span>
                  <span className="font-bold text-green-600">{formatCurrency(calculateAddonTotal())}</span>
                </div>
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 mt-2"
                  onClick={handleAddonCheckout}
                  disabled={processingAddon}
                  data-testid="add-items-to-order-btn"
                >
                  {processingAddon ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Add to My Order — {formatCurrency(calculateAddonTotal())}
                    </>
                  )}
                </Button>
                <p className="text-xs text-stone-500 text-center mt-1">
                  You'll choose your photos & backgrounds first — payment comes after.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reminder to pay for added items before approving */}
        {renderAddonPaymentBanner()}

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setShowUpsellDialog(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Browse All Products
          </Button>
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={handleApprove}
            disabled={approving}
          >
            {approving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Approving...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve Order
              </>
            )}
          </Button>
        </div>

        {/* Upsell Dialog */}
        <Dialog open={showUpsellDialog} onOpenChange={setShowUpsellDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add More Products</DialogTitle>
              <DialogDescription>
                Want to add anything else to your order?
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4 max-h-96 overflow-y-auto">
              {available_products?.filter(p => !(p.name || '').toUpperCase().includes('TEAM PHOTO')).map(product => (
                <Card key={product.id} className="border-stone-200">
                  <CardContent className="p-4">
                    <h3 className="font-medium">{product.name}</h3>
                    <p className="text-lg font-bold text-amber-600">{formatCurrency(product.price)}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2 w-full"
                      onClick={() => {
                        addToAddonCart(product);
                        setShowUpsellDialog(false);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add to Order
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpsellDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upgrade Dialog */}
        {renderUpgradeDialog(available_products, available_packages, preorder.tax_rate || 0, false)}
      </div>
    </div>
  );
};

export default PreOrderReviewPage;
